import { z } from 'zod';
/**
 * Phân quyền 3 tầng — docs/security.md §3 và docs/01-tech-stack.md §6.
 *
 * Frontend dùng chính danh sách này để ẩn/hiện nút; backend dùng nó để chặn.
 * Một nguồn duy nhất, nên không có cảnh nút bấm được mà API trả 403.
 */
export const ProjectRole = z.enum(['PM', 'CONTRIBUTOR', 'VIEWER']);
/**
 * `resource:action`. Thêm quyền mới phải cập nhật bảng ở docs/security.md §3
 * **trong cùng PR** — bảng đó là hợp đồng, không phải tài liệu tham khảo.
 */
export const PERMISSIONS = [
    'project:create',
    'project:read',
    'project:update',
    /**
     * Cột board — thêm, đổi tên, màu, **nhóm** (`category`), WIP, thứ tự, gắn vào
     * sprint, xoá. Người dùng chốt 2026-10-14.
     *
     * Tách khỏi `project:update` để PM cấp **riêng** cho Thành viên (quyền cộng thêm,
     * APM-ROLE-z) hay vai tuỳ biến mà không mở cả cấu hình dự án, thành viên, vai.
     * Ngược lại thì không: ai có `project:update` đều có quyền này (`customGrants`
     * suy ra) — cột từng là một phần của `project:update`, và vai cũ không mất gì.
     *
     * Đổi nhóm cột là đổi **nghĩa** của mọi việc trong cột: cột tên "Đang làm" mà
     * nhóm `todo` khoá tiến độ của mọi thẻ trong đó (dự án SEED, 2026-10-14) — nên
     * mặc định chỉ PM.
     */
    'workflow:manage',
    'task:create',
    'task:update',
    'task:delete',
    'task:assign',
    // `task:edit_actual_hours` (APM-ACTH-b) đã gỡ ở APM-TDRW-d: giờ thực tế chỉ tính
    // từ bấm giờ, không ai sửa tay. Giá trị cũ còn trong `project_role.permissions`
    // bị `AbilityFactory` lọc bỏ (chỉ nhận quyền có trong danh sách này).
    /**
     * Đọc task — APM-GUEST-c, docs/plan-guest-collaborator.md §4.2.
     *
     * Không vai tổ chức hay vai hệ thống nào có nó: `project:read` đã bao trùm. Nó
     * tồn tại để cấp **dạng `own`** (`task:read@own`) cho cộng tác viên ngoài, người
     * không được `project:read` — quyền ấy mở 65 endpoint, gồm replay dựng lại
     * toàn bộ dự án. Chỉ những endpoint khai `anyOf: ['task:read']` mới nhận nó.
     */
    'task:read',
    'document:read',
    /**
     * Upload and edit project documents — APM-PSPACE-i, docs/plan-personal-space.md §3.6.
     *
     * Create, new version, save, reorder, submit for review, and the document
     * branch of `POST /files/presign` + commit. Approve / delete stay on
     * `document:approve`.
     *
     * Split from `task:create`, which used to cover both: the user wants an
     * outside collaborator to upload documents **without** creating tasks, and
     * the other way round. Every system role that has `task:create` has this
     * too, so staff on system roles see no difference.
     */
    'document:write',
    'document:approve',
    'raid:manage',
    'ai_suggestion:approve',
    'change_request:approve',
    'report:read_project',
    'report:read_portfolio',
    'performance:read_project',
    'performance:read_cross_project',
    /**
     * Đặt mục tiêu cá nhân cho nhân sự dưới quyền — APM-PERF2-j.
     *
     * ⚠️ Cố ý ở mức **một cấp** (`direct_report`), khác hẳn hai quyền đọc ngay
     * trên (`report_chain`, toàn nhánh). Đọc được cả nhánh là một chuyện; đặt
     * đích cho người cách hai cấp là chuyện khác, vì người đặt đích phải là
     * người biết tuần sau người ấy làm gì.
     *
     * Không nằm trong `SENSITIVE_PERMISSIONS`: nó **ghi**, không đọc dữ liệu
     * đánh giá của ai. Lịch sử của nó nằm ở `event_log` như mọi mutation khác.
     */
    'performance:set_goal',
    /**
     * Ngân sách dự án — APM-AI-n.
     *
     * `budget:read` xem tổng và xem theo task; `budget:manage` sửa đơn giá, hạng
     * mục chi phí và mức duyệt.
     *
     * ⚠️ Đơn giá theo **người** là dữ liệu nhạy cảm, cùng loại với dữ liệu hiệu
     * suất: đọc nó ghi `sensitive_access_log`. Tool AI mặc định chỉ thấy đơn giá
     * đã **gộp** theo chức vụ hoặc kỹ năng, và chỉ thấy đơn giá theo người khi
     * người bấm nút có `budget:manage`. Một bảng lương rò qua đường AI thì không
     * có cách nào thu lại.
     */
    'budget:read',
    'budget:manage',
    /**
     * Đặt **giá thuê** và **hệ số tốc độ** của thành viên trên một dự án —
     * docs/plan-project-rate-speed.md.
     *
     * ## Vì sao không dùng lại `budget:manage`
     *
     * Tên ấy đã mang **hai** nghĩa ở hai chỗ: ở `BudgetController.overview()` nó
     * là "được xem đơn giá theo người" (hỏi bằng resource **rỗng**, nên chỉ khớp
     * quyền không điều kiện của `ORG_ADMIN`), còn với `PM` nó là "được quản ngân
     * sách dự án mình" (có điều kiện `project_member`). Hai nghĩa ấy tách nhau
     * **chỉ nhờ** mẹo resource rỗng. Chồng nghĩa thứ ba lên là cách chắc chắn để
     * một ngày nào đó ai đó hỏi `can('budget:manage', { projectId })` ở nhánh đọc
     * lương và không ai thấy.
     *
     * ⚠️ Quyền này **không** mở `cost_rate` cấp tổ chức. Giá thuê dự án và lương
     * là hai con số khác bản chất, ở hai bảng khác nhau.
     */
    'project_rate:manage',
    /**
     * Kiểm thử — docs/plan-testing-tab.md §3.
     *
     * Ba mức, và ranh giới quan trọng nhất **không** nằm giữa chúng: **ghi lỗi thì
     * ai có `test:read` cũng làm được**, không cần quyền riêng. Dev tự kiểm thấy
     * lỗi mà phải xin quyền mới ghi được thì họ sẽ nhắn qua chat, và lỗi ấy không
     * vào báo cáo nào.
     *
     * ⚠️ Nhưng **chỉ `test:execute` ghi được kết quả test lại** — tức chỉ tester
     * đóng hoặc mở lại được một lỗi. Đây là cửa giữ cho hai chỉ số *fix yield* và
     * *tỉ lệ mở lại* còn nghĩa: để dev tự xác nhận lỗi của chính mình đã hết thì
     * cả hai con số ấy đo chính lời khai của người bị đo.
     *
     * Vai "Tester" không cần thêm vào enum — theo ADR-007 vai trò dự án **là dữ
     * liệu**, nên PM tạo một vai tuỳ biến mang đúng `test:read` + `test:execute`.
     */
    'test:read',
    'test:execute',
    'test:manage',
    /**
     * Issue vận hành — docs/plan-issue-management.md §8.1.
     *
     * Năm mức, và ranh giới đáng chú ý nằm giữa `report` và `handle`: **ai cũng
     * báo được vấn đề**, kể cả vai Chỉ xem. Cùng lý lẽ với việc ghi lỗi ở
     * `test:read` — người gặp sự cố mà phải xin quyền mới ghi được thì họ nhắn
     * qua chat, và sự cố ấy không vào báo cáo nào, không tính vào SLA nào.
     *
     * ⚠️ `issue:triage` là quyền **đổi mức ưu tiên**, và nó tách khỏi `handle`
     * có chủ đích: người xử lý một việc không nên tự quyết việc ấy gấp đến đâu.
     * Cùng lý lẽ với `test:execute` — để người bị đo tự đặt thước thì thước ấy
     * không còn đo gì nữa.
     *
     * `issue:configure` là sửa **mục tiêu SLA của dự án**: nó viết lại thước đo
     * cho mọi issue tạo sau đó, nên nó đứng riêng khỏi `triage` vốn chỉ đổi một
     * việc.
     *
     * Vai "Trực vận hành" không cần vào enum — ADR-007, PM tạo vai tuỳ biến mang
     * `issue:read` + `issue:report` + `issue:handle`.
     */
    'issue:read',
    'issue:report',
    'issue:handle',
    'issue:triage',
    'issue:configure',
    /**
     * Tab Thiết kế — docs/plan-design-control.md (quyết định 6).
     *
     * `design:read` cho mọi thành viên dự án: màn hình đã duyệt là thứ cả đội làm
     * theo. `design:write` là gắn file Figma, bấm chụp (tiêu hạn mức API Figma của
     * tổ chức), ẩn/hiện màn hình, duyệt hay sửa mô tả và lời thuyết minh — PM, và
     * vai **Designer/BA** qua vai trò tuỳ biến của dự án (ADR-007).
     */
    'design:read',
    'design:write',
    /**
     * Tab Git — docs/plan-git-integration.md §8.7.
     *
     * `git:read` xem tab và khối "Phát triển" trên task: mọi **nhân sự** là thành
     * viên dự án. `git:act` tạo nhánh / MR từ task, gắn hay ẩn một liên kết tay:
     * Thành viên trở lên. `git:manage` dán token, gắn kho, đặt luật tự đổi trạng
     * thái, bật ghi ngược, gắn tài khoản git ↔ người: PM.
     *
     * ⚠️ Cả ba **không bao giờ** tới cộng tác viên ngoài (`GUEST`) hay khách hàng
     * (`CLIENT`), dù vai dự án của họ có mang — tên nhánh, lời commit, tiêu đề MR
     * là chuyện nội bộ của một sản phẩm. Cắt ở `capProjectGrants`
     * (`STAFF_ONLY_PROJECT_PERMISSIONS`, `rbac/permission-matrix.ts`).
     *
     * Máy chủ git (`git_host`) không thuộc ba quyền này: đó là cấu hình của bản cài
     * đặt, `system_config:manage`.
     */
    'git:read',
    'git:act',
    'git:manage',
    'ai_config:manage',
    /** Trang Harness: registry, chạy tool lẻ, run sandbox/replay — APM-H-e. Không nâng quyền dữ liệu. */
    'ai_harness:use',
    'storage_config:manage',
    /**
     * Token Figma **của tổ chức** — docs/plan-design-control.md (quyết định 1).
     *
     * Tách khỏi `design:write`: ai giữ token này thì đọc được **mọi file Figma**
     * mà người tạo token xem được, không riêng file đã gắn vào dự án. Cùng bản chất
     * thông tin xác thực với `storage_config:manage`, nhưng là dữ liệu của một tổ
     * chức chứ không phải của bản cài đặt — nên thuộc `ORG_ADMIN`, không phải
     * `SUPER_ADMIN` (APM-ORG-h8).
     */
    'figma_connection:manage',
    /**
     * Nguồn đồng bộ và máy chủ git **của tổ chức** — APM-ORGINT-a,
     * docs/plan-org-integrations.md.
     *
     * Cùng bản chất `figma_connection:manage`: địa chỉ một hệ thống bên ngoài mà máy
     * chủ ChillPM sẽ gửi token của dự án tới, kèm bí mật app (Lark / Meegle). Dữ liệu
     * của một tổ chức nên thuộc `ORG_ADMIN`; không tách khỏi `user:manage` thì mọi
     * vị trí có quyền quản lý người cũng trỏ được token của dự án về máy mình.
     */
    'integration:manage',
    /**
     * Mẫu tài liệu **của tổ chức** — APM-CD2-k, docs/plan-chilldoc-v2.md §9.1. Đặt một `.chilldoc`
     * làm mẫu, đổi tên, gỡ: chỉ `ORG_ADMIN`. Đọc danh sách thì mọi nhân sự của tổ chức — không cần
     * quyền nào, repository lọc theo tổ chức.
     */
    'document_template:manage',
    /**
     * Cấu hình nhắc bấm giờ đầu ca của tổ chức — APM-TREM-f. Chỉ `ORG_ADMIN`: người dùng chốt
     * 2026-11-02 "đổi từ quản trị của hệ thống về cho role quản trị của tổ chức". Tách khỏi
     * `user:manage` vì quyền ấy `SUPER_ADMIN` cũng có (để gỡ tổ chức bị khoá ngoài).
     */
    'timer_reminder:manage',
    'oauth_config:manage',
    'system_config:manage',
    'user:manage',
    /**
     * Xem sơ đồ phân cấp tổ chức — APM-ORG-b.
     *
     * Tách khỏi `user:manage` vì hai câu hỏi khác nhau: `user:manage` là "được
     * sửa hồ sơ người khác không", còn quyền này là "được **nhìn** cây báo cáo
     * không". `DIRECTOR` và `LEADER` cần vế sau mà không nên có vế trước — điều
     * hành và trưởng nhóm không phải người gán vai trò cho ai.
     *
     * ⚠️ Quyền này KHÔNG mở dữ liệu hiệu suất. Sơ đồ chỉ có tên, vai trò, phòng
     * ban và ai báo cáo cho ai — thứ mà mọi công ty đều dán trên tường. Trộn số
     * đo con người vào đây sẽ biến một sơ đồ tổ chức thành bảng đánh giá, và
     * `performance:read_cross_project` tồn tại chính để canh cửa ấy.
     */
    'org_chart:read',
    /**
     * Gộp / tách tổ chức — APM-ORGMS-c, docs/plan-org-restructure.md §3.7.
     *
     * `propose`: tạo đề xuất, chép link, mời bằng email, sửa cấu hình, huỷ — chỉ `ORG_ADMIN`.
     * `approve`: xem đề xuất của tổ chức mình, duyệt / từ chối, xem cây của bên kia — `ORG_ADMIN`
     * + `DIRECTOR`. Tập **người duyệt** (§3.3) = đúng những người có quyền này ở mỗi bên, nên
     * nút ở giao diện và cửa ở API không lệch nhau.
     */
    'org_restructure:propose',
    'org_restructure:approve',
    'audit_log:read',
    'announcement:broadcast_org',
    /**
     * Dùng AI trên **đồ cá nhân** không thuộc dự án nào — tài liệu của chính mình
     * ở trang Tệp (`format_document`, APM-CD-g). Mọi vai nội bộ đều có; khách hàng
     * không. Không mở dữ liệu của ai khác: skill chỉ đọc thứ người bấm gửi lên.
     * Người dùng duyệt thêm quyền này 2026-09-27.
     */
    'ai:use_personal',
];
export const Permission = z.enum(PERMISSIONS);
/**
 * Quyền đụng tới dữ liệu đánh giá con người. Mỗi lần đọc phải ghi
 * `sensitive_access_log`, không ngoại lệ, kể cả `SUPER_ADMIN`
 * (docs/security.md §5).
 */
export const SENSITIVE_PERMISSIONS = [
    'performance:read_project',
    'performance:read_cross_project',
];
export function isSensitivePermission(permission) {
    return SENSITIVE_PERMISSIONS.includes(permission);
}
/**
 * Quyền hiệu lực của người đang đăng nhập, cho frontend dựng menu.
 *
 * `global` là quyền không phụ thuộc project. `perProject` là quyền chỉ có
 * trong đúng những project được liệt kê — FE cần cả hai để biết nên ẩn nút
 * hay chỉ ẩn ở một số project.
 */
export const AbilitySummary = z.object({
    global: z.array(Permission),
    perProject: z.array(z.object({
        projectId: z.uuid(),
        /**
         * Vai trò trong dự án — APM-ROLE-b.
         *
         * Trước đây là một `ProjectRole` trần. Giờ là object vì một dự án có thể
         * có vai trò **tuỳ biến**: `key` là `null` với chúng, và giao diện phải
         * hiện `name` thô thay vì tra i18n theo `key`. Giữ nguyên chuỗi enum thì
         * frontend không có cách nào phân biệt hai loại, và mọi vai trò tuỳ biến
         * sẽ hiện ra là một khoá i18n thô.
         */
        role: z.object({
            roleId: z.uuid(),
            key: ProjectRole.nullable(),
            name: z.string(),
        }),
        permissions: z.array(Permission),
    })),
    /**
     * Có ai khai người này là quản lý trực tiếp không — APM-PERF-d.
     *
     * ## Vì sao cần một cờ riêng thay vì đọc `global`
     *
     * `global` **cố ý** chỉ chứa quyền không kèm điều kiện: `permissionsOf()` lọc
     * `grant === true`. Mà `LINE_MANAGER_GRANTS` cấp `performance:*` dưới dạng có
     * điều kiện (`direct_report`), nên quyền của quản lý trực tiếp **không bao
     * giờ** xuất hiện ở đây — dù backend cho họ đọc thật.
     *
     * Hệ quả không chỉ là một cờ thiếu: `AllocationTable` gate bằng
     * `global.includes('performance:read_cross_project')`, nên tới trước task này
     * nó **vô hình với mọi quản lý trực tiếp cấp `MEMBER`**. Họ có quyền, có dữ
     * liệu, và không có nút nào để bấm.
     *
     * ## Vì sao đưa nó vào một view không ghi audit là an toàn
     *
     * Đây là **sự thật về chính người đang hỏi**, không nêu tên ai: một boolean,
     * không phải danh sách, không phải số lượng kèm tên. Thứ mà quy ước cấm là
     * đưa *tên người khác* vào một đường đọc không để lại dấu vết — và cờ này
     * không nói gì về người khác ngoài việc "tồn tại ít nhất một".
     */
    hasDirectReports: z.boolean(),
});
//# sourceMappingURL=rbac.js.map